import * as Actions from '../actions/blackoutActions';
import Blackout from '../../entities/blackout';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type BlackoutAction = { 
    type: string; 
    blackout: Blackout | null; 
    blackoutId: string | Blackout; 
    partial: boolean; 
    lastFetch: number; 
    blackouts: Blackout[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Blackout> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Blackout[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Blackout> | null, firmId: string, userId: string, updates?: Blackout[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'blackout', 'blackouts', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Blackout>} data
 * @param {Blackout[]} [updates]
 * @returns {ArrayMap<Blackout>}
 */
function updateStorage(data: ArrayMap<Blackout>, updates?: Blackout[]): ArrayMap<Blackout> {
	if (logging) console.debug('⮂ blackoutReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Blackout[]} [updates]
 * @returns {ArrayMap<Blackout>}
 */
function updateStorageNoReturn(updates?: Blackout[]): void {
	if (logging) console.debug('⮂ blackoutReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Blackout[]} [data]
 * @returns {ArrayMap<Blackout>}
 */
function createState(data?: Blackout[] | ArrayMap<Blackout>): ArrayMap<Blackout> {
	if (!data) return ArrayMapRegistry.clone<Blackout>('blackout', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Blackout>('blackout', data.map(e => e));
    else return ArrayMapRegistry.clone<Blackout>('blackout', data);
}

function blackouts(state: ArrayMap<Blackout>, action: BlackoutAction): ArrayMap<Blackout> {
	if (!state) state = createState();

	let st: ArrayMap<Blackout>;

	switch (action.type) {
		// action.blackout - the new value of blackout with id action.id
		// action.blackoutId - id of the target, used if new value is null
		case Actions.BLACKOUT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ blackoutReducer: FETCH_ONE_SUCCESS', action);

			if (action.blackout === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.blackoutId)) {
				st = state.clone();
				st.set(action.blackout);
				// state.clear();

				updateStorageNoReturn([action.blackout]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.blackouts
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.blackouts
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.blackouts
			 !partial, !stateEmpty, fetchEmpty	-> D return blackouts in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge blackouts in state newer than those in action.blackouts
		 */
		case Actions.BLACKOUT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.blackouts.length === 0;
			const fetched = action.blackouts.map(blackout => new Blackout({...blackout, persisted: true}));

			if (logging) console.debug(`⮂ blackoutReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} blackouts with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ blackoutReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ blackoutReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Blackout> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Blackout[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(blackout => {
					const current = st.get(blackout.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && blackout.status === 'D') {
							// Remove it from state
							toUpdate.push(blackout);
							st.delete(blackout);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (blackout.addedOn > current.addedOn) {
								toUpdate.push(blackout);
								st.set(blackout);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (blackout.status !== 'D') {
							toUpdate.push(blackout);
							st.set(blackout);
						}
						else if (logging) console.debug('⮂ blackoutReducer: partialMerge: skipping deleted blackout not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ blackoutReducer: empty: blackouts', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ blackoutReducer: fullMerge: blackouts', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localBlackout => {
                    // If this blackout has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localBlackout.persisted) {
                        const fetchedBlackout = st.get(localBlackout.id);
                        if (!fetchedBlackout || localBlackout.addedOn > fetchedBlackout.addedOn) st.set(localBlackout);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.BLACKOUT_FETCH_ONE_FAILURE:
			console.error('BLACKOUT_FETCH_ONE_FAILURE', action.blackoutId, action.err);

			// No update to storage required
			return state;

		case Actions.BLACKOUT_FETCH_FAILURE:
			console.error('BLACKOUT_FETCH_FAILURE', action.blackoutId, action.err);

			// No update to storage required
			return state;

		case Actions.BLACKOUT_RESTORE: {
		    let blackout = new Blackout({...action.blackout});
            st = state.clone();

            const existing = st.get(blackout.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (blackout.addedOn >= existing.addedOn) {
                    st.set(blackout);
                    return updateStorage(st, [blackout]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (blackout.status === 'D') return state;

                st.set(blackout);
                return updateStorage(st, [blackout]);
            }
        }
            
		case Actions.BLACKOUT_SAVE: {
		    // console.debug('⮂ BLACKOUT_SAVE reducer', action.blackout?.id);
		    
			// clone the blackout
			let blackout = new Blackout({...action.blackout});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new blackout in the state
			// If this is a create, add to the queue
			const _blackout = st.get(blackout.id);

			if (!_blackout) st.set(blackout);
			else {
				if (blackout.addedOn >= _blackout.addedOn) st.set(blackout);
			}

			return updateStorage(st, [blackout]);
		}

		case Actions.BLACKOUT_SAVE_MANY: {
		    if (logging) console.debug('⮂ BLACKOUT_SAVE_MANY reducer', action.blackouts.map(blackout => blackout.id).join(','));
		    
			// clone the blackouts
			let toProcess = action.blackouts.map(blackout => new Blackout(blackout));

			st = state.clone();
			// state.clear();

			const updates: Blackout[] = [];

			toProcess.forEach(blackout => {
				// Add or update the new blackout in the state
				// If this is a create, add to the queue
				const existing = st.get(blackout.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (blackout.status !== 'D') {
						st.push(blackout);
						updates.push(blackout);
					}
					else if (logging) console.debug('⮂ BLACKOUT_SAVE_MANY: skipping deleted blackout not in state');
				}
				else {
					if (blackout.addedOn >= existing.addedOn) {
						st.set(blackout);
						updates.push(blackout);
					}
					else if (logging) console.debug('⮂ Skipping, existing blackout had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.BLACKOUT_REMOVE: {
			// clone the blackout
			const blackout = new Blackout({...action.blackout});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(blackout);

			return updateStorage(st, [blackout]);
		}

		case Actions.BLACKOUT_RESTORE_FAILURE:
		case Actions.BLACKOUT_SAVE_FAILURE: {
		    console.warn('BLACKOUT_SAVE_FAILURE reducer', action.blackout?.id);
		    if (!action.blackout?.id) {
				console.warn('BLACKOUT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const blackout = ensure(Blackout, action.blackout);
			if (!blackout) {
				console.error('Could not find blackout after call to save');
				return state;
			}

			st = state.clone();

			const _blackoutRef = state.get(blackout.id);

			if (_blackoutRef && (_blackoutRef.addedOn === blackout.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _blackout = new Blackout({..._blackoutRef});
				_blackout.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_blackout.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('blackout', _blackout).then(r => {
						if (r.isErr()) console.error(`⮂ blackoutReducer: failed to save blackout with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ blackoutReducer: saved blackout with fatalError info');
					}).catch(err => console.error(`⮂ blackoutReducer: failed to save blackout with fatalError info: ${err.message}`));

				st.set(_blackout);
			}

			return updateStorage(st);
		}

		case Actions.BLACKOUT_SAVE_MANY_FAILURE: {
		    console.warn('BLACKOUT_SAVE_MANY_FAILURE reducer', action.blackouts.map(blackout => blackout.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.BLACKOUT_RESTORE_SUCCESS:
		case Actions.BLACKOUT_SAVE_SUCCESS: {
			if (!action.blackout?.id) {
				console.warn('BLACKOUT_SAVE_SUCCESS reducer');
                return state;
			}

			const blackoutRef = state.get(action.blackout?.id);
			if (!blackoutRef) {
				console.warn('Could not find blackout after call to save');
                return state;
			}

			st = state.clone();

			if (action.blackout.addedOn >= blackoutRef.addedOn) {
				st.set(action.blackout);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const blackout = new Blackout({...blackoutRef, persisted: true});
				st.set(blackout);
			}

			return updateStorage(st, [action.blackout]);
		}

		case Actions.BLACKOUT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ BLACKOUT_SAVE_MANY_SUCCESS reducer', action.blackouts.map(blackout => blackout.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const blackouts: Blackout[] = [];

			action.blackouts.forEach(e => {
				const blackoutRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!blackoutRef) console.warn('Could not find blackout after call to save');
				else if (e.addedOn >= blackoutRef.addedOn) {
				    st.set(e);
				    blackouts.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const blackout = new Blackout({...blackoutRef, persisted: true});
					st.set(blackout);
					blackouts.push(blackout);
				}
			});

			return updateStorage(st, blackouts);
		}

		case Actions.BLACKOUT_REMOVE_FAILURE: {
		    console.warn('BLACKOUT_REMOVE_FAILURE action', action.blackout?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.BLACKOUT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.BLACKOUT_RESTORE_MANY: {
			if (logging) console.debug('⮂ BLACKOUT_RESTORE_MANY reducer', action.blackouts.map(blackout => blackout.id).join(','));

			const toProcess = action.blackouts.map(blackout => new Blackout({...blackout}));
			st = state.clone();

			const updates: Blackout[] = [];

			toProcess.forEach(blackout => {
				const existing = st.get(blackout.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (blackout.addedOn >= existing.addedOn) {
						st.set(blackout);
						updates.push(blackout);
					}
					else if (logging) console.debug('⮂ BLACKOUT_RESTORE_MANY: skipping older version of blackout');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (blackout.status !== 'D') {
						st.set(blackout);
						updates.push(blackout);
					}
					else if (logging) console.debug('⮂ BLACKOUT_RESTORE_MANY: skipping deleted blackout not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.BLACKOUT_REMOVE_MANY: {
			if (logging) console.debug('⮂ BLACKOUT_REMOVE_MANY reducer', action.blackouts.map(blackout => blackout.id).join(','));

			const toProcess = action.blackouts.map(blackout => new Blackout({...blackout}));
			st = state.clone();

			toProcess.forEach(blackout => {
				st.delete(blackout);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default blackouts;